import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheckIcon, ShieldAlertIcon } from 'lucide-react';

import { api, type AuditRow } from '@/lib/api';
import { useDateTime, useLabel, useT } from '@/lib/i18n';
import { DataTable } from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import type { ColumnDef } from '@tanstack/react-table';

/**
 * Settings → Change log (S3, build/10 §7.4, `09 §9`).
 *
 * The question this screen exists to answer is not a compliance one — it is
 * "why did this item change, and who did it". With several ETCC people editing
 * one catalogue, that stops being answerable the moment there is more than one
 * author, which is the situation a beta creates by definition.
 *
 * READ-ONLY IS STRUCTURAL, NOT A STYLING CHOICE. There is no write route to
 * call: `emit()` on the server is the only producer, and `AuditRepo` exposes no
 * update or delete. This screen could not offer an edit control even if someone
 * added a button for one.
 *
 * The six categories are the primary filter because they are the six kinds of
 * question people arrive with — "who changed the catalogue" (Design), "who
 * touched a credential" (Integration), "was anyone trying to sign in" (Auth).
 */

const CATEGORIES = ['Auth', 'Design', 'Delivery', 'Scoring', 'Integration', 'System'] as const;
const SEVERITIES = ['info', 'notice', 'warning', 'critical'] as const;

const SEVERITY_TONE: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  info: 'outline',
  notice: 'secondary',
  warning: 'destructive',
  critical: 'destructive',
};

const ALL = '__all__';

/** The chain's own integrity, stated rather than assumed. */
function ChainStatus() {
  const t = useT();
  const verify = useQuery({ queryKey: ['audit-verify'], queryFn: api.verifyAudit, retry: false });

  if (verify.isLoading) {
    return <span className="text-muted-foreground text-xs">{t('changeLog.checking')}</span>;
  }
  if (verify.error) {
    return <span className="text-muted-foreground text-xs">{t('changeLog.chainUnavailable')}</span>;
  }

  const report = verify.data!;
  return report.ok ? (
    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
      <ShieldCheckIcon className="size-3.5" />
      {t('changeLog.verified', { count: report.checked })}
    </span>
  ) : (
    <span className="text-destructive flex items-center gap-1.5 text-xs font-medium">
      <ShieldAlertIcon className="size-3.5" />
      {t('changeLog.broken', { at: report.brokenAt ?? '?', reason: report.reason ?? '' })}
    </span>
  );
}

export function ChangeLog() {
  const t = useT();
  const label = useLabel();
  const dateTime = useDateTime();
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<string>(ALL);
  const [severity, setSeverity] = useState<string>(ALL);
  const [actor, setActor] = useState('');

  const params = {
    page,
    pageSize: 25,
    ...(category === ALL ? {} : { category }),
    ...(severity === ALL ? {} : { severity }),
    ...(actor.trim() ? { actor: actor.trim() } : {}),
  };

  const log = useQuery({ queryKey: ['audit', params], queryFn: () => api.audit(params) });

  const columns = useMemo<ColumnDef<AuditRow, unknown>[]>(
    () => [
      {
        header: t('changeLog.column.when'),
        accessorKey: 'ts',
        cell: ({ row }) => (
          <div className="min-w-36">
            <p className="text-sm">{dateTime(row.original.ts)}</p>
            <p className="text-muted-foreground font-mono text-[11px]">#{row.original.seq}</p>
          </div>
        ),
      },
      {
        header: t('changeLog.column.what'),
        accessorKey: 'summary',
        cell: ({ row }) => (
          <div className="min-w-72">
            {/* The summary is written to be read on its own — an operator
                should not have to decode an event name to understand a row. */}
            <p className="text-sm">{row.original.summary}</p>
            <p className="text-muted-foreground font-mono text-[11px]">
              {row.original.event} · {row.original.entityId}
            </p>
          </div>
        ),
      },
      { header: t('changeLog.column.who'), accessorKey: 'actor' },
      {
        header: t('settings.column.category'),
        accessorKey: 'category',
        cell: ({ row }) => (
          <Badge variant="outline">{label('changeLog.category', row.original.category)}</Badge>
        ),
      },
      {
        header: t('changeLog.column.severity'),
        accessorKey: 'severity',
        cell: ({ row }) => (
          <Badge variant={SEVERITY_TONE[row.original.severity] ?? 'outline'}>
            {label('changeLog.severity', row.original.severity)}
          </Badge>
        ),
      },
    ],
    [t, label, dateTime],
  );

  return (
    <div className="grid gap-4">
      <ChainStatus />

      <div className="grid gap-3 sm:grid-cols-3">
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger aria-label={t('changeLog.filterByCategory')}>
            <SelectValue placeholder={t('changeLog.allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('changeLog.allCategories')}</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{label('changeLog.category', c)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={severity} onValueChange={(v) => { setSeverity(v); setPage(1); }}>
          <SelectTrigger aria-label={t('changeLog.filterBySeverity')}>
            <SelectValue placeholder={t('changeLog.allSeverities')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('changeLog.allSeverities')}</SelectItem>
            {SEVERITIES.map((sev) => (
              <SelectItem key={sev} value={sev}>{label('changeLog.severity', sev)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={actor}
          onChange={(e) => { setActor(e.target.value); setPage(1); }}
          placeholder={t('changeLog.filterByWho')}
          aria-label={t('changeLog.filterByActor')}
        />
      </div>

      <DataTable
        columns={columns}
        rows={log.data?.items ?? []}
        isLoading={log.isLoading}
        error={log.error}
        onRetry={() => void log.refetch()}
        emptyTitle={t('changeLog.empty.title')}
        emptyDescription={t('changeLog.empty.description')}
        page={log.data?.page}
        pageSize={log.data?.pageSize}
        total={log.data?.total}
        onPageChange={setPage}
      />
    </div>
  );
}
